import { t } from "@travily/i18n";

const PERCENT = 100;

/**
 * The panel speaks metric keys; a reader does not. Every label is looked up
 * from a literal `t()` call rather than a key built from the key at runtime:
 * the i18n scanner reads literals, and a template key is one nothing can find
 * — neither the missing-translation check nor the unused-key sweep.
 */

export function categoryLabel(key: string): string {
  const labels: Record<string, string> = {
    connectivity: t("components.views.destinations.costs.categories.connectivity"),
    family: t("components.views.destinations.costs.categories.family"),
    food: t("components.views.destinations.costs.categories.food"),
    health: t("components.views.destinations.costs.categories.health"),
    housing: t("components.views.destinations.costs.categories.housing"),
    services: t("components.views.destinations.costs.categories.services"),
    transport: t("components.views.destinations.costs.categories.transport"),
  };
  return labels[key] ?? key;
}

export function itemLabel(key: string): string {
  const labels: Record<string, string> = {
    babysitter_hour: t("components.views.destinations.costs.items.babysitter_hour"),
    bread_500g: t("components.views.destinations.costs.items.bread_500g"),
    car_rental_day: t("components.views.destinations.costs.items.car_rental_day"),
    chicken_1kg: t("components.views.destinations.costs.items.chicken_1kg"),
    childcare_full_time_month: t("components.views.destinations.costs.items.childcare_full_time_month"),
    coffee: t("components.views.destinations.costs.items.coffee"),
    coworking_desk_month: t("components.views.destinations.costs.items.coworking_desk_month"),
    dentist_checkup: t("components.views.destinations.costs.items.dentist_checkup"),
    doctor_visit: t("components.views.destinations.costs.items.doctor_visit"),
    eggs_12: t("components.views.destinations.costs.items.eggs_12"),
    fuel_1l: t("components.views.destinations.costs.items.fuel_1l"),
    groceries_basket: t("components.views.destinations.costs.items.groceries_basket"),
    gym_month: t("components.views.destinations.costs.items.gym_month"),
    haircut: t("components.views.destinations.costs.items.haircut"),
    health_insurance_month: t("components.views.destinations.costs.items.health_insurance_month"),
    internet_broadband_month: t("components.views.destinations.costs.items.internet_broadband_month"),
    intl_school_month: t("components.views.destinations.costs.items.intl_school_month"),
    kids_activity_session: t("components.views.destinations.costs.items.kids_activity_session"),
    laundry_load: t("components.views.destinations.costs.items.laundry_load"),
    meal_inexpensive: t("components.views.destinations.costs.items.meal_inexpensive"),
    meal_midrange_two: t("components.views.destinations.costs.items.meal_midrange_two"),
    milk_1l: t("components.views.destinations.costs.items.milk_1l"),
    mobile_data_month: t("components.views.destinations.costs.items.mobile_data_month"),
    pharmacy_basket: t("components.views.destinations.costs.items.pharmacy_basket"),
    preschool_month: t("components.views.destinations.costs.items.preschool_month"),
    rent_1br_centre: t("components.views.destinations.costs.items.rent_1br_centre"),
    rent_2br_centre: t("components.views.destinations.costs.items.rent_2br_centre"),
    rent_2br_outside: t("components.views.destinations.costs.items.rent_2br_outside"),
    rent_3br_centre: t("components.views.destinations.costs.items.rent_3br_centre"),
    short_stay_apartment: t("components.views.destinations.costs.items.short_stay_apartment"),
    streaming_month: t("components.views.destinations.costs.items.streaming_month"),
    taxi_km: t("components.views.destinations.costs.items.taxi_km"),
    taxi_start: t("components.views.destinations.costs.items.taxi_start"),
    transit_pass_monthly: t("components.views.destinations.costs.items.transit_pass_monthly"),
    transit_ticket_single: t("components.views.destinations.costs.items.transit_ticket_single"),
    utilities_basic: t("components.views.destinations.costs.items.utilities_basic"),
  };
  return labels[key] ?? key;
}

/** The grade as the panel's own header says it: what kind of figure this is,
 *  rather than the badge's shorthand for where it came from. */
function evidenceGradeLabel(grade: string): string {
  const labels: Record<string, string> = {
    inherited: t("components.views.destinations.costs.evidence.inherited"),
    measured: t("components.views.destinations.costs.evidence.measured"),
    modelled: t("components.views.destinations.costs.evidence.modelled"),
  };
  return labels[grade] ?? grade;
}

/** How much of the basket the figure stands on. Full coverage is said in words
 *  because "100% basket coverage" reads as a score rather than a completeness. */
function basketCoverageLabel(coverage: number): string {
  return coverage >= 1
    ? t("components.views.destinations.costs.evidence.coverage_full")
    : t("components.views.destinations.costs.evidence.coverage_partial", { percent: Math.round(coverage * PERCENT) });
}

/** What kind of figure the panel is showing, and how much of the basket it
 *  stands on. A panel that cannot say how much it covers says only the first
 *  half rather than claiming nothing was covered. */
export function evidenceSentence(grade: string, coverage: number | null): string {
  if (coverage === null) return evidenceGradeLabel(grade);

  return t("components.views.destinations.costs.evidence.sentence", {
    grade: evidenceGradeLabel(grade),
    coverage: basketCoverageLabel(coverage),
  });
}

export function gradeLabel(grade: string): string {
  const labels: Record<string, string> = {
    inherited: t("components.views.destinations.costs.grades.inherited"),
    measured: t("components.views.destinations.costs.grades.measured"),
    modelled: t("components.views.destinations.costs.grades.modelled"),
  };
  return labels[grade] ?? grade;
}

export function tierLabel(tier: string): string {
  const labels: Record<string, string> = {
    high: t("components.views.destinations.costs.household.tiers.high"),
    low: t("components.views.destinations.costs.household.tiers.low"),
    mid: t("components.views.destinations.costs.household.tiers.mid"),
  };
  return labels[tier] ?? tier;
}
